import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval, createStorePerformanceRequest } from '../../../../../constants';
import { useThemeContext } from '../../../../../global-state/themeContext';
import { BopisData, useAsyncAction, useRfid } from '../../../../../hooks';
import { Icons } from '../../../../../stories/atoms';
import { ColumnFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { DynamicTab, TabContentProps, TabLabelProps } from '../../../../../stories/molecules';
import { homeTranslations } from '../../../../../translations';
import { isFailureResponse } from '../../../../../utils';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { BopisOrdersContent } from './BopisOrdersContent';
import { OverviewContent } from './overviewContent/OverviewContent';

export type BopisWidgetProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
};

export const BopisWidget: FC<BopisWidgetProps> = observer(({ interval, currentDate }) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const { fetchBopisData } = useRfid();
  const { bopis: bopisTranslations } = homeTranslations;

  const [tabIndex, setTabIndex] = useState(0);

  const {
    result: bopisData,
    isLoading: bopisDataLoading,
    isError: bopisDataError,
    triggerAction: triggerFetchBopisData,
  } = useAsyncAction<BopisData>();

  const fetchData = useCallback(async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });

    const response = await fetchBopisData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }
    return response.data;
  }, [interval, currentDate]);

  const fetchWidgetData = () => {
    triggerFetchBopisData(fetchData);
  };

  const handleTabChange = (event: React.SyntheticEvent, newTabIndex: number) => {
    setTabIndex(newTabIndex);
  };

  useEffect(() => {
    fetchWidgetData();
  }, [currentDate]);

  const totalPendingOrders = useMemo(() => {
    if (!bopisData) return 0;

    const {
      pending: { notStarted, partiallyPicked, fullyPicked },
    } = bopisData;
    return notStarted + partiallyPicked + fullyPicked;
  }, [bopisData]);

  const totalProcessedOrders = useMemo(() => {
    if (!bopisData) return 0;

    const {
      processed: { cancelled, zeroPicked, partiallyFulfilled, fullyFulfilled },
    } = bopisData;
    return cancelled + zeroPicked + partiallyFulfilled + fullyFulfilled;
  }, [bopisData]);

  const totalOrders = totalPendingOrders + totalProcessedOrders;

  const isNoData = totalOrders === 0;

  const tabsLabels: TabLabelProps[] = [
    {
      tabName: t(bopisTranslations.tabOverviewName.key, bopisTranslations.tabOverviewName.defaultValue),
      isTabVisible: true,
    },
    {
      tabName: t(bopisTranslations.tabBopisOrdersName.key, bopisTranslations.tabBopisOrdersName.defaultValue),
      isTabVisible: true,
    },
  ];

  const tabsContents: TabContentProps[] = [
    {
      tabContent: bopisData ? <OverviewContent bopisData={bopisData} /> : <></>,
      visible: true,
    },
    {
      tabContent: bopisData ? (
        <BopisOrdersContent
          bopisData={bopisData}
          totalOrders={totalOrders}
          totalProcessedOrders={totalProcessedOrders}
        />
      ) : (
        <></>
      ),
      visible: true,
    },
  ];

  return (
    <WidgetCardContent
      headerIcon={Icons.PROFILE}
      headerText={t(bopisTranslations.title.key, bopisTranslations.title.defaultValue)}
      subHeaderText={t(bopisTranslations.subTitle.key, bopisTranslations.subTitle.defaultValue)}
      isLoading={bopisDataLoading}
      isError={bopisDataError}
      errorText={t(bopisTranslations.error.key, bopisTranslations.error.defaultValue)}
      toolTipText={t(bopisTranslations.toolTip.key, bopisTranslations.toolTip.defaultValue)}
      isNoData={isNoData}
      noDataText={t(bopisTranslations.noDataText.key, bopisTranslations.noDataText.defaultValue)}
      lastUpdatedDate={bopisData?.lastUpdated}
      content={
        bopisData && (
          <ColumnFlexContainer flex={1} data-testid="bopis-widget-wrapper" height="100%">
            <DynamicTab
              tabLabelArray={tabsLabels}
              handleTabChange={handleTabChange}
              tabValue={tabIndex}
              variant="scrollable"
              tabBgColor={mode === 'light' ? 'baseGray' : 'backgroundTableHeaderDark'}
              content={tabsContents}
              fullHeight
            />
          </ColumnFlexContainer>
        )
      }
    />
  );
});
